import {
  Button,
  Input,
  InputRef,
  List,
  Modal,
  Row,
  Tabs,
} from 'antd';
import React, {
  FC, useEffect, useRef, useState,
} from 'react';
import { Header } from 'antd/es/layout/layout';
import { Link } from 'react-router-dom';
import { ProjectSummary } from '@ynomia/core/dist/blueprint';
import MdiIcon from '@mdi/react';
import { mdiFolderOpenOutline } from '@mdi/js';
import { REDIRECT_TO } from '../../../config/constants';
import { getCurrentRoute } from '../../../services';
import { getProjectId, getProjectName, getProjects } from '../../../selectors';
import { Icon, SideMenu } from '../../atoms';
import { getContextStores } from '../../../context';
import styles from './styles.module.less';
import { useWindowSize } from '../../../hooks';

const { Search } = Input;

const MenuHeader: FC<React.PropsWithChildren<{}>> = ({ children }) => {
  /* Context  */
  const contextStores = getContextStores();
  const { layoutState } = contextStores;
  const { presenterModeActive } = layoutState;

  /* Selectors */
  const projectName = getProjectName(contextStores);
  const projectId = getProjectId(contextStores);
  const projects = getProjects(contextStores);

  const { width } = useWindowSize();
  const [tab, setTab] = useState('active');
  const searchRef = useRef<InputRef>(null);
  const [isProjectsModalOpen, setIsArchivedProjectsModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [isUsingArrowKeys, setIsUsingArrowKeys] = useState(false);

  useEffect(() => {
    if (isProjectsModalOpen) {
      setTimeout(() => {
        if (searchRef.current) searchRef.current.focus();
      }, 0);
    }
  }, [isProjectsModalOpen]);

  const getProjectSwitchUrl = (requestedProjectId: string) => {
    if (!requestedProjectId) return '';
    const currentRoute = getCurrentRoute();
    if (!currentRoute) {
      return `/${requestedProjectId}`;
    }
    const newPath = currentRoute?.path;
    return `/${requestedProjectId}?`
      + `${REDIRECT_TO}=${encodeURIComponent(newPath)}`;
  };

  const getProjectSwitchItem = (
    { name, id }: { name: string, id: string },
  ) => ({
    label: name,
    key: id,
    url: getProjectSwitchUrl(id),
  });

  const menuActiveProjects = projects?.filter(
    ({ isArchived }) => !isArchived,
  );

  const menuArchivedProjects = projects?.filter(
    ({ isArchived }) => isArchived,
  );

  const getInitials = (name: string) => name
    .split(/\s+/)
    .map(word => word[0] ?? '')
    .join('');

  const filterBySearchText = <T extends { name: string; tenant: string }>(
    items?: T[],
    text = '',
  ) => {
    const lower = text.toLowerCase();
    return items?.filter(({ name, tenant }) => {
      const searchTargets = [name, getInitials(name), tenant];
      return searchTargets.some(target => target.toLowerCase().includes(lower));
    }) || [];
  };

  const filteredActiveProjects = filterBySearchText(menuActiveProjects, searchText);

  const filteredArchivedProjects = filterBySearchText(menuArchivedProjects, searchText);

  const renderLeftCol = () => (
    <>
      <div className={styles.title}>
        {!presenterModeActive && <SideMenu />}
        <Link to="/">
          <Icon name="ynomiaFullLogoDark" style={{ width: 130 }} id="ynomia-logo-menu-header" />
        </Link>
      </div>
      <div className={styles.menuProject}>
        <Button
          style={{ height: 40 }}
          onClick={() => setIsArchivedProjectsModalOpen(true)}
          icon={(
            <MdiIcon
              path={mdiFolderOpenOutline}
              size={0.8}
              style={{ marginBottom: -1 }}
            />
          )}
        >
          {projectName || ''}
        </Button>
      </div>
    </>
  );

  const renderHeaderOnMultipleLine = () => (
    <div>
      <Row className={styles.containerMultiple} gutter={[16, 4]}>
        {renderLeftCol()}
      </Row>
      {!presenterModeActive && (
        <div style={{ paddingLeft: 20, paddingRight: 20, marginBottom: 15 }}>
          {children}
        </div>
      )}
    </div>
  );

  const renderHeader = () => (
    <div className={styles.container}>
      {renderLeftCol()}
      <div className={styles.childrenColumn}>{children}</div>
    </div>
  );

  if (presenterModeActive) {
    return (
      <Header className={styles.headerPresenterMode}>
        <div className={styles.containerPresenterMode}>
          <div className={styles.title}>
            <Link to="/">
              <Icon name="ynomiaFullLogoDark" style={{ width: 130 }} id="ynomia-logo-menu-header" />
            </Link>
          </div>
          <span className={styles.projectTitlePresenterMode}>
            {projectName || ''}
          </span>
          {children}
        </div>
      </Header>
    );
  }

  const renderChildren = (filteredProjects: ProjectSummary[]) => (
    <List
      size="small"
      style={{ height: 500, overflow: 'auto' }}
      dataSource={filteredProjects.map(getProjectSwitchItem)}
      renderItem={(item, index) => {
        const isActive = index === activeIndex;
        const isCurrentProject = item.key === projectId;
        return (
          <a
            href={item.url}
            style={{ width: '100%' }}
          >
            <List.Item
              className={isCurrentProject ? styles.projectItemCurrent : styles.projectItem}
              style={isActive ? { backgroundColor: '#F7F7F7', textDecoration: 'underline' } : {}}
              onMouseOver={() => {
                setIsUsingArrowKeys(false);
                setActiveIndex(index);
              }}
              ref={
                elem => isActive && isUsingArrowKeys
                && elem?.scrollIntoView({ block: 'center' })
              }
            >
              { item.label }
            </List.Item>
          </a>
        );
      }}
    />
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    const currentList = tab === 'active' ? filteredActiveProjects : filteredArchivedProjects;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsUsingArrowKeys(true);
      setActiveIndex(i => Math.min(i + 1, currentList.length - 1));
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIsUsingArrowKeys(true);
      setActiveIndex(i => Math.max(i - 1, 0));
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      setIsUsingArrowKeys(true);
      const selectedProject = currentList[activeIndex];
      if (selectedProject) {
        window.location.href = getProjectSwitchUrl(selectedProject.id);
      }
    }
  };

  return (
    <Header className={styles.header}>
      <Modal
        title="Projects"
        open={isProjectsModalOpen}
        footer={null}
        onCancel={() => setIsArchivedProjectsModalOpen(false)}
        destroyOnClose
      >
        <Search
          style={{ marginBottom: 10 }}
          placeholder="Search"
          onChange={(e) => {
            setActiveIndex(0);
            setSearchText(e.target.value);
          }}
          allowClear
          // Auto focus
          ref={searchRef}
          onKeyDown={onKeyDown}
        />
        <Tabs
          type="card"
          size="small"
          onChange={key => setTab(key)}
          items={
            [
              {
                label: `Active (${filteredActiveProjects?.length})`,
                key: 'active',
                children: renderChildren(filteredActiveProjects),
              },
              {
                label: `Archived (${filteredArchivedProjects.length})`,
                key: 'archived',
                children: renderChildren(filteredArchivedProjects),
              },
            ]
          }
        />
      </Modal>
      {width > 1239 ? renderHeader() : renderHeaderOnMultipleLine()}
    </Header>
  );
};

export default MenuHeader;
